import { useRef, useEffect, useCallback } from "react";

export default function BeatVisualizer({ audioRef, playing, color = "#00f3ff" }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);

  const initAudio = useCallback(() => {
    if (!audioRef?.current || analyserRef.current) return;
    try {
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ac.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      const source = ac.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ac.destination);
      analyserRef.current = analyser;
      sourceRef.current = source;
    } catch (e) {
      // Audio context already created for this element
    }
  }, [audioRef]);

  useEffect(() => {
    if (playing) initAudio();
  }, [playing, initAudio]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctxRef.current = ctx;
    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2;

    const bufLen = analyser ? analyser.frequencyBinCount : 64;
    const dataArr = new Uint8Array(bufLen);
    if (analyser) analyser.getByteFrequencyData(dataArr);

    ctx.clearRect(0, 0, W, H);

    // Background glow
    const avgFreq = analyser ? dataArr.reduce((a, b) => a + b, 0) / bufLen : 30;
    const glowIntensity = avgFreq / 255;
    const grad = ctx.createRadialGradient(cx, cy, 10, cx, cy, cx * 0.9);
    grad.addColorStop(0, `rgba(0, 243, 255, ${0.03 + glowIntensity * 0.08})`);
    grad.addColorStop(0.5, `rgba(255, 0, 255, ${0.02 + glowIntensity * 0.04})`);
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    const bars = 64;
    const step = Math.floor(bufLen / bars);
    const baseRadius = Math.min(cx, cy) * 0.25;
    const maxBarH = Math.min(cx, cy) * 0.55;

    // Circular spectrum bars
    for (let i = 0; i < bars; i++) {
      const val = analyser ? dataArr[i * step] : (30 + Math.sin(Date.now() * 0.002 + i * 0.3) * 20);
      const norm = val / 255;
      const angle = (i / bars) * Math.PI * 2 - Math.PI / 2;
      const barH = maxBarH * norm;

      const x1 = cx + Math.cos(angle) * baseRadius;
      const y1 = cy + Math.sin(angle) * baseRadius;
      const x2 = cx + Math.cos(angle) * (baseRadius + barH);
      const y2 = cy + Math.sin(angle) * (baseRadius + barH);

      // Bar gradient
      const hue = (i / bars) * 60 + 170; // cyan to magenta
      ctx.strokeStyle = `hsla(${hue}, 100%, ${50 + norm * 30}%, ${0.5 + norm * 0.5})`;
      ctx.lineWidth = Math.max(1.5, (W / bars) * 0.4);
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // Glow dot at tip
      if (norm > 0.4) {
        ctx.fillStyle = `hsla(${hue}, 100%, 70%, ${norm * 0.8})`;
        ctx.beginPath();
        ctx.arc(x2, y2, 1.5 + norm * 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Inner circle pulse
    const pulse = 1 + glowIntensity * 0.15;
    ctx.strokeStyle = `rgba(0, 243, 255, ${0.2 + glowIntensity * 0.4})`;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, baseRadius * pulse, 0, Math.PI * 2);
    ctx.stroke();

    // Second inner ring
    ctx.strokeStyle = `rgba(255, 0, 255, ${0.1 + glowIntensity * 0.3})`;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.arc(cx, cy, baseRadius * pulse * 0.7, 0, Math.PI * 2);
    ctx.stroke();

    // Particles
    const time = Date.now() * 0.001;
    for (let p = 0; p < 12; p++) {
      const pAngle = (p / 12) * Math.PI * 2 + time * 0.3;
      const pRadius = baseRadius + maxBarH * 0.3 + Math.sin(time * 2 + p) * maxBarH * 0.2 * glowIntensity;
      const px = cx + Math.cos(pAngle) * pRadius;
      const py = cy + Math.sin(pAngle) * pRadius;
      const pSize = 1 + glowIntensity * 2;
      ctx.fillStyle = `rgba(0, 243, 255, ${0.3 + glowIntensity * 0.5})`;
      ctx.beginPath();
      ctx.arc(px, py, pSize, 0, Math.PI * 2);
      ctx.fill();
    }

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resizeCanvas = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext("2d");
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resizeCanvas();
    animRef.current = requestAnimationFrame(draw);
    window.addEventListener("resize", resizeCanvas);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resizeCanvas);
    };
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-10"
      data-testid="beat-visualizer"
    />
  );
}
